import React, { useEffect, useMemo, useState } from 'react';
import { m } from 'framer-motion';
import {
  FiArrowRight,
  FiDroplet,
  FiMail,
  FiTrendingDown,
  FiZap,
  FiShield,
  FiAlertTriangle
} from 'react-icons/fi';
import Header from './Header';
import Footer from './Footer';
import CookieBanner from './CookieBanner';
import { SITE_ORIGIN, internalUrl } from './siteConfig';
import {
  WEBCODECRAFT_MONTHLY_GBP,
  HOURLY_RATE_GBP,
  calculateWebsiteTax,
  reinvestInsight
} from './websiteTaxCalculator';
import { DEFAULT_PAGE_TITLE } from './seoDefaults';

const PATH = '/website-tax-calculator';
const CANONICAL = `${SITE_ORIGIN}${PATH}`;

const steps = ['setup', 'fiddle', 'speed', 'value'];

function formatMoney(n) {
  return `£${Math.round(n).toLocaleString('en-GB')}`;
}

function WebsiteTaxCalculatorPage() {
  const [phase, setPhase] = useState('hero');
  const [stepIndex, setStepIndex] = useState(0);
  const [setup, setSetup] = useState(null);
  const [fiddle, setFiddle] = useState(null);
  const [speed, setSpeed] = useState(null);
  const [customerValue, setCustomerValue] = useState(500);
  const [roas, setRoas] = useState(0);
  const [email, setEmail] = useState('');

  useEffect(() => {
    document.title = 'Website Tax Calculator — See what DIY & agency sites really cost | WebCodeCraft';
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', CANONICAL);
    return () => {
      document.title = DEFAULT_PAGE_TITLE;
      if (canonical) canonical.setAttribute('href', `${SITE_ORIGIN}/`);
    };
  }, []);

  const result = useMemo(() => {
    if (!setup || !fiddle || !speed) return null;
    return calculateWebsiteTax({
      setup,
      fiddle,
      speed,
      customerValueGbp: customerValue
    });
  }, [setup, fiddle, speed, customerValue]);

  const reinvest = useMemo(() => {
    if (!result || !roas) return 0;
    return reinvestInsight(result.U, roas);
  }, [result, roas]);

  const progress = useMemo(() => {
    if (phase === 'hero') return 0;
    if (phase === 'result') return 100;
    return ((stepIndex + 1) / steps.length) * 100;
  }, [phase, stepIndex]);

  const startAuditor = () => {
    setPhase('auditor');
    setStepIndex(0);
  };

  const goNext = () => {
    if (stepIndex < steps.length - 1) setStepIndex((i) => i + 1);
    else setPhase('result');
  };

  const mailtoAudit = () => {
    if (!result) return;
    const subject = encodeURIComponent('My Website Tax estimate — WebCodeCraft');
    const body = encodeURIComponent(
      `Please send my Website Tax summary.\n\n` +
        `Estimated year-1 "status quo": ${formatMoney(result.statusQuoYear1)}\n` +
        `WebCodeCraft (illustrative ${formatMoney(WEBCODECRAFT_MONTHLY_GBP)}/mo): ${formatMoney(result.webcodecraftYear1)}\n` +
        `Illustrative difference: ${formatMoney(result.savingsVsWcc)}\n\n` +
        (email ? `Email: ${email}\n` : '')
    );
    window.location.href = `mailto:hello@webcodecraft.info?subject=${subject}&body=${body}`;
  };

  return (
    <>
      <Header />
      <main id="main-content" className="pt-20 min-h-screen min-w-0 overflow-x-clip bg-gradient-to-b from-slate-50 via-white to-indigo-50/30">
        <section className="container mx-auto px-4 py-12 md:py-16 max-w-4xl text-center">
          <m.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-rose-500/15 to-indigo-500/15 mb-6"
            aria-hidden
          >
            <FiDroplet className="w-8 h-8 text-indigo-600" />
          </m.div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Stop paying the &quot;website tax&quot;
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto mb-8 leading-relaxed">
            Many UK and South West businesses lose thousands in year one to hidden costs — upfront builds, your time on fixes, and
            leads lost to slow or clunky sites. Use this 60-second estimate to see where the leak might be.
          </p>
          {phase === 'hero' && (
            <m.button
              type="button"
              onClick={startAuditor}
              className="btn btn-primary btn-large inline-flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              See how much you could save
              <FiArrowRight className="w-5 h-5" />
            </m.button>
          )}
          <p className="text-xs text-gray-500 mt-6 max-w-xl mx-auto">
            Illustrative model only — not financial or tax advice. Packages and fees on our site apply at signup.
          </p>
        </section>

        {phase === 'auditor' && (
          <section className="container mx-auto px-4 pb-12 max-w-2xl" aria-label="Website tax auditor questions">
            <div className="mb-8">
              <div className="flex justify-between text-sm text-gray-500 mb-2">
                <span>Progress</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-indigo-500 to-cyan-500 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {stepIndex === 0 && (
              <AuditorCard title="1. Your current setup" subtitle="What best describes your site today?">
                <div className="grid gap-3 sm:grid-cols-1">
                  <ChoiceCard
                    selected={setup === 'diy'}
                    onClick={() => setSetup('diy')}
                    label="I have a DIY site"
                  />
                  <ChoiceCard
                    selected={setup === 'agency'}
                    onClick={() => setSetup('agency')}
                    label="I have an old agency site"
                  />
                  <ChoiceCard
                    selected={setup === 'nothing'}
                    onClick={() => setSetup('nothing')}
                    label="I don’t have a site yet"
                  />
                </div>
                <NextRow disabled={!setup} onNext={goNext} />
              </AuditorCard>
            )}

            {stepIndex === 1 && (
              <AuditorCard
                title="2. Monthly &quot;fiddle&quot; time"
                subtitle="Roughly how long do you spend fixing or updating the site each month?"
              >
                <div className="grid gap-3">
                  <ChoiceCard
                    selected={fiddle === '0'}
                    onClick={() => setFiddle('0')}
                    label="0 hours"
                  />
                  <ChoiceCard
                    selected={fiddle === '1-3'}
                    onClick={() => setFiddle('1-3')}
                    label="1–3 hours"
                  />
                  <ChoiceCard
                    selected={fiddle === '5+'}
                    onClick={() => setFiddle('5+')}
                    label="5+ hours (help!)"
                  />
                </div>
                <NextRow disabled={!fiddle} onNext={goNext} />
              </AuditorCard>
            )}

            {stepIndex === 2 && (
              <AuditorCard title="3. Site speed &amp; polish" subtitle="How does your site feel on mobile?">
                <div className="grid gap-3">
                  <ChoiceCard
                    selected={speed === 'fast'}
                    onClick={() => setSpeed('fast')}
                    label="Fast"
                  />
                  <ChoiceCard
                    selected={speed === 'slow'}
                    onClick={() => setSpeed('slow')}
                    label="Slow"
                  />
                  <ChoiceCard
                    selected={speed === 'unsure'}
                    onClick={() => setSpeed('unsure')}
                    label="Not sure (we’ll assume slower risk)"
                  />
                </div>
                <NextRow disabled={!speed} onNext={goNext} />
              </AuditorCard>
            )}

            {stepIndex === 3 && (
              <AuditorCard
                title="4. Value of one new customer"
                subtitle="Roughly what is a typical new customer worth to you? (moves the &quot;performance&quot; part of the estimate.)"
              >
                <div className="px-1 py-4">
                  <div className="flex justify-between text-sm font-medium text-gray-700 mb-2">
                    <span>{formatMoney(50)}</span>
                    <span className="text-indigo-600">{formatMoney(customerValue)}</span>
                    <span>{formatMoney(5000)}+</span>
                  </div>
                  <input
                    type="range"
                    min={50}
                    max={5000}
                    step={50}
                    value={customerValue}
                    onChange={(e) => setCustomerValue(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                    aria-valuemin={50}
                    aria-valuemax={5000}
                    aria-valuenow={customerValue}
                  />
                </div>
                <NextRow disabled={false} onNext={goNext} label="See my estimate" />
              </AuditorCard>
            )}
          </section>
        )}

        {phase === 'result' && result && (
          <section className="container mx-auto px-4 pb-16 max-w-4xl">
            <m.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-3xl border border-gray-200 bg-white shadow-xl overflow-hidden"
            >
              <div className="bg-gradient-to-r from-slate-900 to-indigo-900 text-white px-6 py-8 text-center">
                <p className="text-sm uppercase tracking-wide text-indigo-200 mb-2">Your illustrative year-1 comparison</p>
                <p className="text-3xl sm:text-4xl font-bold">
                  Website tax estimate:{' '}
                  <span className="text-rose-300">{formatMoney(result.websiteTax)}</span>
                </p>
                <p className="text-indigo-100 mt-2 text-sm max-w-lg mx-auto">
                  Difference between a typical &quot;status quo&quot; year and our illustrative managed fee (
                  {formatMoney(WEBCODECRAFT_MONTHLY_GBP)}/mo × 12).
                </p>
              </div>

              <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100">
                <div className="p-6 md:p-8">
                  <h2 className="flex items-center gap-2 text-lg font-bold text-gray-900 mb-4">
                    <span className="text-rose-500" aria-hidden>
                      <FiTrendingDown />
                    </span>
                    Status quo (illustrative)
                  </h2>
                  <ul className="space-y-3 text-gray-700 text-sm">
                    <li className="flex justify-between gap-4">
                      <span>Upfront / capital lock-up</span>
                      <span className="font-semibold text-gray-900">{formatMoney(result.U)}</span>
                    </li>
                    <li className="flex justify-between gap-4">
                      <span>
                        Time cost ({result.T} hrs/mo × {formatMoney(HOURLY_RATE_GBP)}/hr × 12)
                      </span>
                      <span className="font-semibold text-gray-900">{formatMoney(result.burnoutAnnual)}</span>
                    </li>
                    <li className="flex justify-between gap-4">
                      <span>Performance / opportunity (est.)</span>
                      <span className="font-semibold text-gray-900">{formatMoney(result.performanceAnnual)}</span>
                    </li>
                    <li className="flex justify-between gap-4 pt-3 border-t border-gray-100 font-bold text-base">
                      <span>Total year 1</span>
                      <span className="text-rose-600">{formatMoney(result.statusQuoYear1)}</span>
                    </li>
                  </ul>
                </div>
                <div className="p-6 md:p-8 bg-emerald-50/50">
                  <h2 className="flex items-center gap-2 text-lg font-bold text-gray-900 mb-4">
                    <span className="text-emerald-600" aria-hidden>
                      <FiShield />
                    </span>
                    WebCodeCraft (illustrative)
                  </h2>
                  <ul className="space-y-3 text-gray-700 text-sm">
                    <li className="flex justify-between gap-4">
                      <span>Upfront build (typical model)</span>
                      <span className="font-semibold text-emerald-700">£0</span>
                    </li>
                    <li className="flex justify-between gap-4">
                      <span>Managed hosting &amp; service (×12)</span>
                      <span className="font-semibold text-gray-900">{formatMoney(result.webcodecraftYear1)}</span>
                    </li>
                    <li className="flex justify-between gap-4 pt-3 border-t border-emerald-100 font-bold text-base">
                      <span>Total year 1 (est.)</span>
                      <span className="text-emerald-700">{formatMoney(result.webcodecraftYear1)}</span>
                    </li>
                  </ul>
                  <p className="mt-4 text-xs text-gray-600">
                    £0 upfront refers to our positioning; your actual package is agreed in writing — see{' '}
                    <a href={internalUrl('services')} className="text-indigo-600 underline">
                      services
                    </a>
                    .
                  </p>
                </div>
              </div>
            </m.div>

            <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-6 max-w-xl mx-auto">
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Optional: if you kept upfront capital for ads, assumed ROAS (×)
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min={0}
                  max={5}
                  step={0.5}
                  value={roas}
                  onChange={(e) => setRoas(Number(e.target.value))}
                  className="flex-1 h-2 bg-gray-200 rounded-lg accent-indigo-600"
                />
                <span className="text-sm font-mono w-12">{roas}x</span>
              </div>
              {reinvest > 0 && (
                <p className="mt-3 text-sm text-gray-700">
                  <FiZap className="inline text-amber-500 mr-1" aria-hidden />
                  Illustrative: reinvesting avoided upfront ({formatMoney(result.U)}) at {roas}x ROAS →{' '}
                  <strong>{formatMoney(reinvest)}</strong> revenue (hypothetical).
                </p>
              )}
            </div>

            <div className="mt-10 rounded-2xl bg-gradient-to-r from-indigo-600 to-cyan-600 p-8 text-white text-center max-w-xl mx-auto">
              <h3 className="text-xl font-bold mb-2 flex items-center justify-center gap-2">
                <FiMail /> Email my summary
              </h3>
              <p className="text-sm text-indigo-100 mb-4">
                Add your email (optional) and we’ll open a pre-filled message you can send to us.
              </p>
              <input
                type="email"
                placeholder="you@company.co.uk"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full max-w-sm mx-auto block px-4 py-3 rounded-xl text-gray-900 mb-4"
                autoComplete="email"
              />
              <button
                type="button"
                onClick={mailtoAudit}
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-indigo-700 font-semibold rounded-xl hover:bg-gray-100"
              >
                Email my audit &amp; savings plan
              </button>
              <p className="text-xs text-indigo-200 mt-4">
                Or{' '}
                <a href={internalUrl('contact')} className="underline font-medium">
                  go to contact form
                </a>
              </p>
            </div>

            <div className="mt-12 text-center">
              <button
                type="button"
                onClick={() => {
                  setPhase('hero');
                  setStepIndex(0);
                  setSetup(null);
                  setFiddle(null);
                  setSpeed(null);
                  setCustomerValue(500);
                  setRoas(0);
                }}
                className="text-indigo-600 font-medium hover:underline"
              >
                Start over
              </button>
            </div>
          </section>
        )}

        <section className="container mx-auto px-4 py-16 max-w-3xl border-t border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">What makes up the &quot;website tax&quot;?</h2>
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <div className="flex gap-4">
              <FiAlertTriangle className="w-6 h-6 text-amber-500 shrink-0 mt-1" aria-hidden />
              <div>
                <h3 className="font-semibold text-gray-900">Opportunity cost</h3>
                <p className="text-sm mt-1">
                  Visitors who leave because a page is slow or unclear — we model part of this from your customer value
                  and speed answers.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <FiAlertTriangle className="w-6 h-6 text-amber-500 shrink-0 mt-1" aria-hidden />
              <div>
                <h3 className="font-semibold text-gray-900">Technical debt</h3>
                <p className="text-sm mt-1">
                  DIY stacks and ageing builds often need emergency fixes — time and stress that could go on your core
                  business.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <FiAlertTriangle className="w-6 h-6 text-amber-500 shrink-0 mt-1" aria-hidden />
              <div>
                <h3 className="font-semibold text-gray-900">Capital lock-up</h3>
                <p className="text-sm mt-1">
                  Large upfront agency fees tie up cash that could fund ads, stock, or hiring — our model is built around
                  reducing that barrier.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <CookieBanner />
    </>
  );
}

function AuditorCard({ title, subtitle, children }) {
  return (
    <m.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-gray-200 bg-white p-6 md:p-8 shadow-lg"
    >
      <h2 className="text-xl font-bold text-gray-900 mb-1">{title}</h2>
      <p className="text-sm text-gray-600 mb-6">{subtitle}</p>
      {children}
    </m.div>
  );
}

function ChoiceCard({ selected, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`w-full text-left px-5 py-4 rounded-xl border-2 transition-all font-medium ${
        selected
          ? 'border-indigo-600 bg-indigo-50 text-indigo-900 shadow-md'
          : 'border-gray-200 bg-gray-50/80 hover:border-indigo-300 text-gray-800'
      }`}
    >
      {label}
    </button>
  );
}

function NextRow({ disabled, onNext, label = 'Continue' }) {
  return (
    <div className="mt-8 flex justify-end">
      <m.button
        type="button"
        disabled={disabled}
        onClick={onNext}
        className="btn btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
        whileTap={{ scale: disabled ? 1 : 0.98 }}
      >
        {label}
        <FiArrowRight className="inline ml-2 w-4 h-4" />
      </m.button>
    </div>
  );
}

export default WebsiteTaxCalculatorPage;
